/**
 * Character Sprite Loader
 *
 * Loads character sprites from PNG files and converts them to SpriteData format.
 * PNG format: 112×96 pixels (7 frames × 16px wide, 3 direction rows × 32px tall)
 * Row 0 = down, Row 1 = up, Row 2 = right
 * Frame order: walk1, walk2, walk3, type1, type2, read1, read2
 */

import type { SpriteData } from "../types/index.js";
// Import character PNGs via Vite for correct path handling
import char0Url from "../assets/characters/char_0.png";
import char1Url from "../assets/characters/char_1.png";
import char2Url from "../assets/characters/char_2.png";
import char3Url from "../assets/characters/char_3.png";
import char4Url from "../assets/characters/char_4.png";
import char5Url from "../assets/characters/char_5.png";
import char6Url from "../assets/characters/char_6.png";
import char7Url from "../assets/characters/char_7.png";
import char8Url from "../assets/characters/char_8.png";
import char9Url from "../assets/characters/char_9.png";
import char10Url from "../assets/characters/char_10.png";
import char11Url from "../assets/characters/char_11.png";

// Character sprite dimensions
const SPRITE_WIDTH = 16;
const SPRITE_HEIGHT = 32; // Full frame height (includes 8px top padding)
const FRAMES_PER_ROW = 7;
const DIRECTION_ROWS = 3;

// PNG paths (resolved by Vite) - 12 unique character variations
const CHARACTER_PNG_PATHS = [
  char0Url,
  char1Url,
  char2Url,
  char3Url,
  char4Url,
  char5Url,
  char6Url,
  char7Url,
  char8Url,
  char9Url,
  char10Url,
  char11Url,
];

export interface LoadedCharacterData {
  down: SpriteData[]; // 7 frames
  up: SpriteData[]; // 7 frames
  right: SpriteData[]; // 7 frames
}

// Store loaded character data
let loadedCharacters: LoadedCharacterData[] | null = null;
let loadingPromise: Promise<LoadedCharacterData[]> | null = null;

/**
 * Convert ImageData pixel to hex color string
 */
function pixelToHex(r: number, g: number, b: number, a: number): string {
  // Transparent pixel
  if (a < 128) return "";
  // Convert to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Extract a single sprite frame from an image
 */
function extractFrame(
  imageData: ImageData,
  frameX: number,
  frameY: number,
  width: number,
  height: number,
): SpriteData {
  const sprite: SpriteData = [];

  for (let y = 0; y < height; y++) {
    const row: string[] = [];
    for (let x = 0; x < width; x++) {
      const px = frameX + x;
      const py = frameY + y;
      const idx = (py * imageData.width + px) * 4;
      const r = imageData.data[idx];
      const g = imageData.data[idx + 1];
      const b = imageData.data[idx + 2];
      const a = imageData.data[idx + 3];
      row.push(pixelToHex(r, g, b, a));
    }
    sprite.push(row);
  }

  return sprite;
}

/**
 * Load a single character PNG and extract all frames
 */
async function loadCharacterPNG(path: string): Promise<LoadedCharacterData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      // Create canvas to read pixel data
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d")!;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Extract frames for each direction
      const down: SpriteData[] = [];
      const up: SpriteData[] = [];
      const right: SpriteData[] = [];

      // Row 0 = down, Row 1 = up, Row 2 = right
      const directions = [down, up, right];

      for (let dirIdx = 0; dirIdx < DIRECTION_ROWS; dirIdx++) {
        for (let frame = 0; frame < FRAMES_PER_ROW; frame++) {
          const frameX = frame * SPRITE_WIDTH;
          const frameY = dirIdx * SPRITE_HEIGHT;
          const sprite = extractFrame(imageData, frameX, frameY, SPRITE_WIDTH, SPRITE_HEIGHT);
          directions[dirIdx].push(sprite);
        }
      }

      resolve({ down, up, right });
    };

    img.onerror = () => {
      reject(new Error(`Failed to load character sprite: ${path}`));
    };

    img.src = path;
  });
}

/**
 * Load all character sprites from PNG files
 */
export async function loadCharacterSprites(): Promise<LoadedCharacterData[]> {
  // Return cached data if already loaded
  if (loadedCharacters) {
    return loadedCharacters;
  }

  // Return existing promise if already loading
  if (loadingPromise) {
    return loadingPromise;
  }

  // Start loading
  loadingPromise = Promise.all(
    CHARACTER_PNG_PATHS.map((path) => loadCharacterPNG(path).catch(() => null)),
  ).then((results) => {
    // Filter out failed loads
    const loaded = results.filter((r): r is LoadedCharacterData => r !== null);

    if (loaded.length > 0) {
      loadedCharacters = loaded;
      console.log(`[PixelOffice] Loaded ${loaded.length} character sprites from PNG`);
    } else {
      console.warn("[PixelOffice] No character sprites loaded from PNG, using fallback templates");
    }

    return loaded;
  });

  return loadingPromise;
}

/**
 * Get loaded character data (null if not yet loaded)
 */
export function getLoadedCharacters(): LoadedCharacterData[] | null {
  return loadedCharacters;
}

/**
 * Check if character sprites have been loaded
 */
export function areCharacterSpritesLoaded(): boolean {
  return loadedCharacters !== null && loadedCharacters.length > 0;
}

/**
 * Clear loaded sprites (for testing/reset)
 */
export function clearLoadedCharacters(): void {
  loadedCharacters = null;
  loadingPromise = null;
}
